import type { AppMode } from '@iheartjane/types';
import { Storage } from '@iheartjane/utils';
import { useSyncExternalStore } from 'react';

type LegacyCartUuidRead = (
  appMode: AppMode,
  storeId?: string | number
) => string | null;

type LegacyCartUuidClear = (appMode: AppMode) => void;

/**
 * App-mode-scoped only, unlike `cartStorageKey` (redux version), which also scopes the three
 * embed modes by store. Moot until something writes this key on those modes.
 */
const cartUuidStorageKey = (appMode: AppMode) => `cart-uuid-${appMode}`;

/** Owns the client-side cart uuid creation (non-redux) */
export const createLocalCartUuidStore = ({
  legacyClear,
  legacyRead,
}: {
  legacyClear?: LegacyCartUuidClear;
  legacyRead?: LegacyCartUuidRead;
} = {}) => {
  const listeners = new Set<() => void>();

  const subscribe = (listener: () => void) => {
    listeners.add(listener);
    return () => {
      listeners.delete(listener);
    };
  };

  const getLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number
  ): string | null =>
    Storage.get<string>(cartUuidStorageKey(appMode)) ||
    legacyRead?.(appMode, storeId) ||
    null;

  const updateLocalCartUuid = (appMode: AppMode, cartUuid: string | null) => {
    const key = cartUuidStorageKey(appMode);

    if (cartUuid) {
      Storage.set(key, cartUuid);
    } else {
      Storage.remove(key);
    }

    // On any write, not just a clear: once this module answers for the uuid,
    // the adoption source has served its purpose and must not outlive it.
    // TODO(carts-v3): remove once rolled out
    legacyClear?.(appMode);

    listeners.forEach((listener) => listener());
  };

  /**
   * Subscribed read to ensure it logs the uuid when one is created. `enabled`
   * gates the read, not the hook: `getSnapshot` hits localStorage on every call.
   */
  const useLocalCartUuid = (
    appMode: AppMode,
    storeId?: string | number,
    enabled = true
  ) =>
    useSyncExternalStore(
      subscribe,
      () => (enabled ? getLocalCartUuid(appMode, storeId) : null),
      () => null
    );

  return { getLocalCartUuid, updateLocalCartUuid, useLocalCartUuid };
};
